/**
 * Dashboard API - integrates with optimus.Ink.backend Dashboard endpoints.
 * Uses getApiBaseUrl from api.js and optional Bearer token for auth.
 */

import { getApiBaseUrl, getBaseHeaders } from "./api";

/**
 * @param {{ accessToken?: string }} options
 * @returns {Headers}
 */
function getHeaders({ accessToken } = {}) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return headers;
}

/**
 * Build query string from params object. Omits profile_id when it is 'all' or undefined.
 * @param {Record<string, string|number|undefined>} params
 * @returns {string}
 */
function buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v == null || v === "") return;
        if (k === "profile_id" && (v === "all" || v === "")) return;
        search.set(k, String(v));
    });
    const q = search.toString();
    return q ? `?${q}` : "";
}

/**
 * GET /api/dashboard/summary - KPIs + chart + acquisition in one call
 * @param {{ profile_id?: string|number, date_from?: string, date_to?: string, granularity?: string, accessToken?: string }} params
 */
export async function fetchSummary({
    profile_id = "all",
    date_from,
    date_to,
    granularity,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id, date_from, date_to, granularity });
    const res = await fetch(`${base}/api/dashboard/summary${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/kpis
 * @param {{ profile_id?: string|number, accessToken?: string }} options
 * @returns {Promise<{ ok: boolean, data?: { visitors, revenue, conversionRate, revenuePerVisitor, bounceRate, sessionTime, onlineNow }, error?: string }>}
 */
export async function fetchKpis({ profile_id = "all", accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id });
    const res = await fetch(`${base}/api/dashboard/kpis${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/chart
 * @param {{ profile_id?: string|number, granularity?: string, date_from?: string, date_to?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: array, error?: string }>}
 */
export async function fetchChart({
    profile_id = "all",
    granularity,
    date_from,
    date_to,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id, granularity, date_from, date_to });
    const res = await fetch(`${base}/api/dashboard/chart${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? [] };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/realtime/visitors
 * @param {{ profile_id?: string|number, accessToken?: string }} options
 * @returns {Promise<{ ok: boolean, data?: array, error?: string }>}
 */
export async function fetchRealtimeVisitors({
    profile_id = "all",
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id });
    const res = await fetch(`${base}/api/dashboard/realtime/visitors${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? [] };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/realtime/activity
 * @param {{ profile_id?: string|number, accessToken?: string }} options
 * @returns {Promise<{ ok: boolean, data?: array, error?: string }>}
 */
export async function fetchRealtimeActivity({
    profile_id = "all",
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id });
    const res = await fetch(`${base}/api/dashboard/realtime/activity${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? [] };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/acquisition
 * @param {{ profile_id?: string|number, group_by?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: array, error?: string }>}
 */
export async function fetchAcquisition({
    profile_id = "all",
    group_by = "channel",
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id, group_by });
    const res = await fetch(`${base}/api/dashboard/acquisition${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? [] };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/geo
 * @param {{ profile_id?: string|number, group_by?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: array, error?: string }>}
 */
export async function fetchGeo({
    profile_id = "all",
    group_by = "country",
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id, group_by });
    const res = await fetch(`${base}/api/dashboard/geo${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? [] };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/tech
 * @param {{ profile_id?: string|number, group_by?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: array, error?: string }>}
 */
export async function fetchTech({
    profile_id = "all",
    group_by = "browser",
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id, group_by });
    const res = await fetch(`${base}/api/dashboard/tech${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? [] };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/host
 * @param {{ profile_id?: string|number, group_by?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: array, error?: string }>}
 */
export async function fetchHost({
    profile_id = "all",
    group_by = "hostname",
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id, group_by });
    const res = await fetch(`${base}/api/dashboard/host${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? [] };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/journey
 * @param {{ profile_id?: string|number, accessToken?: string }} options
 * @returns {Promise<{ ok: boolean, data?: { goalsTotalVisitors, goals, funnelStages, journeyGoal, journeyPayment }, error?: string }>}
 */
export async function fetchJourney({ profile_id = "all", accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ profile_id });
    const res = await fetch(`${base}/api/dashboard/journey${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? {} };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/dashboard/visitors
 * @param {{ profile_id?: string|number, q?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: array, error?: string }>}
 */
export async function fetchVisitors({
    profile_id = "all",
    q,
    accessToken,
} = {}) {
    const base = getApiBaseUrl();
    const query = buildQuery({ profile_id, q });
    const res = await fetch(`${base}/api/dashboard/visitors${query}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data ?? [] };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}
